/* ==========================================================================
   BYOB BUILDER — VANILLA JS
   No jQuery, no frameworks. Uses the Shopify AJAX Cart API only.
   Event delegation is used throughout so there is exactly ONE listener
   per interaction type per section instance (no duplicate listeners,
   even if products/cards are re-rendered).
   ========================================================================== */

(function () {
  'use strict';

  /**
   * Small debounce/rAF batching helper so rapid clicks only trigger
   * one DOM paint of the sticky bar per animation frame instead of one
   * per click.
   */
  function batchWithRaf(fn) {
    let scheduled = false;
    return function (...args) {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        fn.apply(this, args);
      });
    };
  }

  // Markers wrapped around the itemized box contents inside the cart
  // note. Used so a customer rebuilding their box before checkout gets
  // their note block REPLACED, not duplicated, while anything else
  // already in the note (e.g. a gift message) is left untouched.
  const NOTE_BLOCK_START = '----- BYOB Box Contents -----';
  const NOTE_BLOCK_END = '----- End Box Contents -----';

  /**
   * One controller instance per .byob-builder section on the page.
   * Keeps everything scoped so multiple instances never collide.
   */
  class BYOBBuilder {
    constructor(root) {
      this.root = root;
      this.grid = root.querySelector('[data-product-grid]');
      this.stickyBar = root.querySelector('[data-sticky-bar]');
      this.selectedCountEl = root.querySelector('[data-selected-count]');
      this.previewEl = root.querySelector('[data-selected-preview]');
      this.checkoutBtn = root.querySelector('[data-checkout-btn]');
      this.ctaLabel = root.querySelector('[data-cta-label]');
      this.ctaSpinner = root.querySelector('[data-cta-spinner]');
      this.counterWrap = root.querySelector('[data-counter-wrap]');
      this.errorEl = root.querySelector('[data-error-message]');

      // ---- Box Summary panel refs (product name + qty breakdown) ----
      this.summaryToggle = root.querySelector('[data-summary-toggle]');
      this.summaryPanel = root.querySelector('[data-summary-panel]');
      this.summaryList = root.querySelector('[data-summary-list]');
      this.summaryClose = root.querySelector('[data-summary-close]');
      this.summaryOverlay = root.querySelector('[data-summary-overlay]');
      this.summaryCountEl = root.querySelector('[data-summary-count]');

      // ---- Config pulled from data-attributes (set via Liquid/schema) ----
      this.requiredQuantity = parseInt(root.dataset.requiredQuantity, 10) || 20;
      this.defaultMaxQuantity = parseInt(root.dataset.defaultMaxQuantity, 10) || 5;
      this.bundleVariantId = parseInt(root.dataset.bundleVariantId, 10) || 0;
      this.bundlePriceText = root.dataset.bundlePriceText || '';
      this.bundleType = root.dataset.bundleType || 'Build Your Own Box';
      this.buttonText = root.dataset.buttonText || 'Checkout';

      // selections: Map<productId, { variantId, title, qty, max, image }>
      this.selections = new Map();
      this.isSubmitting = false;

      this.renderStickyBarBatched = batchWithRaf(this.renderStickyBar.bind(this));

      this.bindEvents();
      this.initCards();
      this.setStickyBarHeightVar();
      this.renderStickyBar();
    }

    /** Seed the selections map with every card at qty 0. */
    initCards() {
      if (!this.grid) return;
      const cards = this.grid.querySelectorAll('[data-product-card]');
      cards.forEach((card) => {
        const productId = card.dataset.productId;
        this.selections.set(productId, {
          variantId: card.dataset.variantId,
          title: card.dataset.productTitle,
          image: card.dataset.productImage,
          qty: 0,
          max: parseInt(card.dataset.max, 10) || this.defaultMaxQuantity,
        });
      });
    }

    /** Single delegated click listener covers every plus/minus button. */
    bindEvents() {
      if (this.grid) {
        this.grid.addEventListener('click', (event) => {
          const addBtn = event.target.closest('[data-add-btn]');
          const plusBtn = event.target.closest('[data-qty-plus]');
          const minusBtn = event.target.closest('[data-qty-minus]');
          if (addBtn) {
            this.changeQuantity(addBtn.closest('[data-product-card]'), 1);
          } else if (plusBtn) {
            this.changeQuantity(plusBtn.closest('[data-product-card]'), 1);
          } else if (minusBtn) {
            this.changeQuantity(minusBtn.closest('[data-product-card]'), -1);
          }
        });
      }

      if (this.checkoutBtn) {
        this.checkoutBtn.addEventListener('click', () => this.handleCheckout());
      }

      // ---- Box Summary panel open/close ----
      if (this.summaryToggle) {
        this.summaryToggle.addEventListener('click', () => this.toggleSummaryPanel());
      }
      if (this.summaryClose) {
        this.summaryClose.addEventListener('click', () => this.closeSummaryPanel());
      }
      if (this.summaryOverlay) {
        this.summaryOverlay.addEventListener('click', () => this.closeSummaryPanel());
      }

      window.addEventListener('resize', () => this.setStickyBarHeightVar());
    }

    get totalSelected() {
      let total = 0;
      this.selections.forEach((item) => {
        total += item.qty;
      });
      return total;
    }

    /**
     * Increase/decrease a single product's quantity, respecting:
     *  - that product's own max quantity
     *  - the global required-quantity ceiling (never allow more than required)
     */
    changeQuantity(card, delta) {
      if (!card) return;
      const productId = card.dataset.productId;
      const item = this.selections.get(productId);
      if (!item) return;

      const remainingSlots = this.requiredQuantity - this.totalSelected;
      let nextQty = item.qty + delta;

      if (delta > 0) {
        if (item.qty >= item.max) return; // per-product cap reached
        if (remainingSlots <= 0) return; // global cap reached
      }
      if (delta < 0 && item.qty <= 0) return;

      nextQty = Math.max(0, Math.min(nextQty, item.max));
      item.qty = nextQty;
      this.selections.set(productId, item);

      this.renderCard(card, item);
      this.renderStickyBarBatched();
    }

    /** Update a single card's UI: count, button states, selected styling. */
    renderCard(card, item) {
      const countEl = card.querySelector('[data-qty-count]');
      const minusBtn = card.querySelector('[data-qty-minus]');
      const plusBtn = card.querySelector('[data-qty-plus]');
      const addBtn = card.querySelector('[data-add-btn]');
      const qtySelector = card.querySelector('[data-qty-selector]');
      const remainingSlots = this.requiredQuantity - this.totalSelected;

      if (countEl) {
        // "1 added" / "2 added" ... matches the reference gray pill design.
        countEl.textContent = item.qty + ' added';
        countEl.classList.remove('is-pulsing');
        // Force reflow so the animation can re-trigger on consecutive clicks.
        void countEl.offsetWidth;
        countEl.classList.add('is-pulsing');
      }

      if (minusBtn) minusBtn.disabled = item.qty <= 0;
      if (plusBtn) plusBtn.disabled = item.qty >= item.max || remainingSlots <= 0;
      if (addBtn) addBtn.disabled = item.qty === 0 && remainingSlots <= 0;

      // Swap between "Add to Box" and the quantity selector: the button
      // is what a customer sees first; once qty > 0 the quantity selector
      // takes over so they can adjust it further.
      if (addBtn && qtySelector) {
        addBtn.hidden = item.qty > 0;
        qtySelector.hidden = item.qty === 0;
      }

      card.classList.toggle('is-selected', item.qty > 0);
    }

    /**
     * Re-render every card's plus button state. Needed whenever the global
     * remaining-slot count changes, since a card that wasn't at its own max
     * may still need to be disabled once the 20-item ceiling is hit.
     */
    refreshAllCardButtons() {
      if (!this.grid) return;
      const remainingSlots = this.requiredQuantity - this.totalSelected;
      this.grid.querySelectorAll('[data-product-card]').forEach((card) => {
        const item = this.selections.get(card.dataset.productId);
        const plusBtn = card.querySelector('[data-qty-plus]');
        const addBtn = card.querySelector('[data-add-btn]');
        if (item) {
          if (plusBtn) plusBtn.disabled = item.qty >= item.max || remainingSlots <= 0;
          if (addBtn) addBtn.disabled = item.qty === 0 && remainingSlots <= 0;
        }
      });
    }

    /** Paint the sticky bottom bar: counter, thumbnails, CTA state. */
    renderStickyBar() {
      const total = this.totalSelected;
      const remaining = this.requiredQuantity - total;
      const isComplete = total === this.requiredQuantity;

      if (this.selectedCountEl) {
        this.selectedCountEl.textContent = total;
        this.selectedCountEl.classList.remove('is-pulsing');
        void this.selectedCountEl.offsetWidth;
        this.selectedCountEl.classList.add('is-pulsing');
      }

      if (this.counterWrap) {
        this.counterWrap.classList.toggle('is-complete', isComplete);
      }

      // ---- Selected product thumbnail preview (capped, +N for the rest) ----
      if (this.previewEl) {
        const MAX_PREVIEW = 5;
        const fragment = document.createDocumentFragment();
        const selectedItems = [];
        this.selections.forEach((item) => {
          if (item.qty > 0) selectedItems.push(item);
        });

        selectedItems.slice(0, MAX_PREVIEW).forEach((item) => {
          if (item.image) {
            const img = document.createElement('img');
            img.src = item.image;
            img.alt = '';
            img.loading = 'lazy';
            img.className = 'byob-sticky-bar__preview-thumb';
            fragment.appendChild(img);
          }
        });

        if (selectedItems.length > MAX_PREVIEW) {
          const more = document.createElement('span');
          more.className = 'byob-sticky-bar__preview-more';
          more.textContent = '+' + (selectedItems.length - MAX_PREVIEW);
          fragment.appendChild(more);
        }

        this.previewEl.innerHTML = '';
        this.previewEl.appendChild(fragment);
      }

      // ---- "View box summary" toggle label ----
      if (this.summaryToggle) {
        this.summaryToggle.textContent = total > 0 ? `View box summary (${total})` : 'View box summary';
        this.summaryToggle.hidden = total === 0;
      }

      // ---- CTA button state/label ----
      if (this.checkoutBtn && this.ctaLabel) {
        if (isComplete) {
          this.ctaLabel.textContent = this.buttonText;
          this.checkoutBtn.disabled = false;
          this.checkoutBtn.classList.add('is-ready');
        } else {
          const remainingText =
            remaining > 0
              ? `Add ${remaining} More Item${remaining === 1 ? '' : 's'}`
              : this.buttonText;
          this.ctaLabel.textContent = remainingText;
          this.checkoutBtn.disabled = true;
          this.checkoutBtn.classList.remove('is-ready');
        }
      }

      this.clearError();
      this.refreshAllCardButtons();
      this.renderSummaryPanel();
    }

    // ------------------------------------------------------------------
    // BOX SUMMARY PANEL
    // Itemized "what's actually in this box" list, shown on demand so the
    // customer always knows exactly which products + quantities they've
    // picked — independent of whether the cart/checkout/Smart Cart UI
    // ever renders it for them.
    // ------------------------------------------------------------------

    /** Cache the sticky bar's live height as a CSS var so the summary
     *  panel's content never sits underneath the fixed checkout bar. */
    setStickyBarHeightVar() {
      if (!this.stickyBar) return;
      const height = this.stickyBar.offsetHeight;
      if (height) this.root.style.setProperty('--byob-sticky-bar-h', height + 'px');
    }

    toggleSummaryPanel() {
      if (!this.summaryPanel) return;
      const isOpen = this.summaryPanel.classList.contains('is-open');
      if (isOpen) this.closeSummaryPanel();
      else this.openSummaryPanel();
    }

    openSummaryPanel() {
      if (!this.summaryPanel) return;
      this.setStickyBarHeightVar();
      this.renderSummaryPanel();
      this.summaryPanel.classList.add('is-open');
      if (this.summaryOverlay) this.summaryOverlay.classList.add('is-open');
    }

    closeSummaryPanel() {
      if (!this.summaryPanel) return;
      this.summaryPanel.classList.remove('is-open');
      if (this.summaryOverlay) this.summaryOverlay.classList.remove('is-open');
    }

    /** Rebuild the visible list of selected products + quantities. */
    renderSummaryPanel() {
      if (!this.summaryList) return;

      const selectedItems = [];
      this.selections.forEach((item) => {
        if (item.qty > 0) selectedItems.push(item);
      });

      if (this.summaryCountEl) {
        this.summaryCountEl.textContent = String(this.totalSelected);
      }

      if (!selectedItems.length) {
        this.summaryList.innerHTML =
          '<p class="byob-summary-panel__empty">You haven\u2019t added any items yet.</p>';
        return;
      }

      const fragment = document.createDocumentFragment();
      selectedItems.forEach((item) => {
        const row = document.createElement('div');
        row.className = 'byob-summary-row';

        if (item.image) {
          const img = document.createElement('img');
          img.className = 'byob-summary-row__thumb';
          img.src = item.image;
          img.alt = '';
          img.loading = 'lazy';
          row.appendChild(img);
        }

        const name = document.createElement('span');
        name.className = 'byob-summary-row__name';
        name.textContent = item.title;
        row.appendChild(name);

        const qty = document.createElement('span');
        qty.className = 'byob-summary-row__qty';
        qty.textContent = 'x' + item.qty;
        row.appendChild(qty);

        fragment.appendChild(row);
      });

      this.summaryList.innerHTML = '';
      this.summaryList.appendChild(fragment);
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }

    setSubmitting(state) {
      this.isSubmitting = state;
      if (this.checkoutBtn) this.checkoutBtn.disabled = state || this.totalSelected !== this.requiredQuantity;
      if (this.ctaSpinner) this.ctaSpinner.hidden = !state;
      if (this.ctaLabel) this.ctaLabel.style.opacity = state ? '0.6' : '1';
    }

    /**
     * Build the { "Rajma": 5, "Haldi": 3, ... , "Bundle Type": "..." }
     * line item properties object from current selections. Kept for
     * structured data (CSV exports, admin order page, apps that DO read
     * properties) — but see updateCartNote() below for the guaranteed-
     * visible copy of the same information.
     */
    buildLineItemProperties() {
      const properties = {};
      this.selections.forEach((item) => {
        if (item.qty > 0) {
          properties[item.title] = String(item.qty);
        }
      });
      properties['Bundle Type'] = this.bundleType;
      return properties;
    }

    /** Human-readable "- Product x Qty" block, wrapped in markers. */
    buildNoteBlock() {
      const lines = [];
      this.selections.forEach((item) => {
        if (item.qty > 0) lines.push('- ' + item.title + ' x ' + item.qty);
      });
      const header = this.bundleType + ' (' + this.requiredQuantity + ' items) \u2014 PACK THESE:';
      return NOTE_BLOCK_START + '\n' + header + '\n' + lines.join('\n') + '\n' + NOTE_BLOCK_END;
    }

    /** Remove a previously-written BYOB block from an existing note,
     *  so re-building a box before checkout replaces it instead of
     *  stacking duplicate blocks. Anything else in the note is kept. */
    stripPreviousNoteBlock(existingNote) {
      if (!existingNote) return '';
      const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const pattern = new RegExp(
        escapeRegex(NOTE_BLOCK_START) + '[\\s\\S]*?' + escapeRegex(NOTE_BLOCK_END),
        'g'
      );
      return existingNote.replace(pattern, '').trim();
    }

    /**
     * Writes the itemized box contents into the CART / ORDER NOTE.
     *
     * Why: the bundle product is a single line item (qty 1) — the real
     * 20 products only exist as line item properties on that one line.
     * Shiprocket's Smart Cart drawer and most invoice/packing-slip apps
     * do NOT render custom line item properties, which is exactly why
     * the summary wasn't showing to the customer OR on the invoice.
     *
     * order.note, on the other hand, is rendered by default on:
     *  - the Shopify admin order page
     *  - the default order confirmation email
     *  - virtually every invoice/packing-slip app (incl. Order Printer)
     * ...regardless of theme or app customization, so this is the most
     * reliable place to guarantee the warehouse (and the customer) can
     * see exactly what's inside the box.
     */
    async updateCartNote() {
      const cart = await this.fetchCart();
      const cleanedExisting = this.stripPreviousNoteBlock(cart.note || '');
      const newBlock = this.buildNoteBlock();
      const combinedNote = cleanedExisting ? cleanedExisting + '\n\n' + newBlock : newBlock;

      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: combinedNote }),
      });
      if (!res.ok) throw new Error('Unable to save your box summary to the order.');
      return res.json();
    }

    /** GET the current cart contents. */
    async fetchCart() {
      const res = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error('Unable to read cart.');
      return res.json();
    }

    /**
     * Remove any existing line(s) for the bundle variant so a customer
     * re-building their box never ends up with duplicate bundle lines.
     * Cart line "numbers" shift after each removal, so lines are removed
     * one at a time, always re-checking the freshest cart state.
     */
    async clearExistingBundleLines() {
      let cart = await this.fetchCart();
      let bundleLine = cart.items.findIndex((item) => item.variant_id === this.bundleVariantId);

      while (bundleLine !== -1) {
        const lineNumber = bundleLine + 1; // Shopify line numbers are 1-indexed
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: lineNumber, quantity: 0 }),
        });
        if (!res.ok) throw new Error('Unable to clear the previous bundle from your cart.');
        cart = await res.json();
        bundleLine = cart.items.findIndex((item) => item.variant_id === this.bundleVariantId);
      }
    }

    /**
     * Refresh cart-count elements and fire the common cart-update events/
     * pub-sub hooks so the theme (and Shiprocket's own listeners) pick up
     * the change. Mirrors the same approach used in the upsell popup script.
     */
    async refreshCartState() {
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        const cart = await res.json();

        document
          .querySelectorAll('.cart-count-bubble, [data-cart-count], .cart-count')
          .forEach((el) => {
            el.textContent = cart.item_count;
          });

        ['cart:updated', 'cart:refresh', 'cart:change', 'cart:build'].forEach((name) => {
          document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: { cart } }));
        });

        if (window.publish && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
          try {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, { cart: cart, source: 'byob-builder' });
          } catch (err) {
            // Non-fatal — theme just doesn't use this pattern.
          }
        }

        return cart;
      } catch (err) {
        console.error('[BYOB] Cart state refresh failed:', err);
      }
    }

    /**
     * Locates the Shiprocket Smart Cart's container inside its Shadow DOM
     * (same element/selector identified while building the upsell popup —
     * see project notes: shadow host -> #shiprocket-mini-cart-container).
     * Cached once found.
     */
    findSmartCartContainer() {
      if (this._smartCartContainer) return this._smartCartContainer;
      const all = document.querySelectorAll('*');
      for (let i = 0; i < all.length; i++) {
        const root = all[i].shadowRoot;
        if (!root) continue;
        const container = root.querySelector('#shiprocket-mini-cart-container');
        if (container) {
          this._smartCartContainer = container;
          return container;
        }
      }
      return null;
    }

    /**
     * Opens Shiprocket's mini-cart drawer directly (instead of redirecting
     * to /cart) once the bundle/products are added, so the customer sees
     * their box right away. Uses Shiprocket's own global refresh/show
     * functions (found via DevTools inspection — updateMiniCartSection,
     * srShowMiniCartDrawer, slideShiprocketSmartCartInFrame) so the drawer
     * re-renders with the just-added items rather than stale content.
     * Falls back to a plain /cart redirect if the drawer can't be located
     * (e.g. Shiprocket markup changes on this store in the future).
     */
    openSmartCartDrawer() {
      const container = this.findSmartCartContainer();
      if (!container) {
        window.location.href = '/cart';
        return;
      }

      var candidates = ['updateMiniCartSection', 'srShowMiniCartDrawer', 'slideShiprocketSmartCartInFrame'];
      candidates.forEach(function (fnName) {
        try {
          if (typeof window[fnName] === 'function') {
            window[fnName]();
          }
        } catch (err) {
          console.error('[BYOB] Shiprocket refresh fn "' + fnName + '" threw:', err);
        }
      });
    }
    async addBundleToCart(properties) {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: this.bundleVariantId,
              quantity: 1,
              properties: properties,
            },
          ],
        }),
      });

      if (!res.ok) {
        const errorBody = await res.json().catch(() => ({}));
        throw new Error(errorBody.description || 'Unable to add the bundle to your cart.');
      }
      return res.json();
    }

    /**
     * Fallback path used when no bundle product is configured for this
     * section (bundle product is optional — a merchant may not want to run
     * a special offer price and can just leave it unset). Adds each
     * selected product to the cart individually, at its own regular price,
     * so checkout still works either way.
     *
     * Requests are sent ONE AT A TIME (not Promise.all) — firing several
     * /cart/add.js calls in parallel races against Shopify's cart session
     * and can randomly fail one of them.
     */
    async addIndividualProductsToCart() {
      const itemsToAdd = [];
      this.selections.forEach((item) => {
        if (item.qty > 0) {
          itemsToAdd.push({ id: item.variantId, quantity: item.qty });
        }
      });

      for (const entry of itemsToAdd) {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [entry] }),
        });
        if (!res.ok) {
          const errorBody = await res.json().catch(() => ({}));
          throw new Error(errorBody.description || 'Unable to add products to your cart.');
        }
      }
    }

    /**
     * Full checkout flow: validate → add to cart → save box-summary note
     * → redirect. If a bundle product is configured, use it (special
     * bundle price, selections attached as line item properties + note).
     * If not, the bundle product is optional — fall back to adding each
     * selected product individually at its own regular price, so
     * checkout always works.
     */
    async handleCheckout() {
      if (this.isSubmitting) return;
      this.clearError();

      // ---- Hard validation: never allow anything but exactly "required" items ----
      if (this.totalSelected !== this.requiredQuantity) {
        this.showError(`Please select exactly ${this.requiredQuantity} items.`);
        return;
      }

      this.setSubmitting(true);

      try {
        if (this.bundleVariantId) {
          const properties = this.buildLineItemProperties();
          await this.clearExistingBundleLines();
          await this.addBundleToCart(properties);
        } else {
          await this.addIndividualProductsToCart();
        }

        // Save the itemized "what's actually in this box" breakdown to
        // the order note — this is what makes the summary reliably show
        // up for both the customer (order confirmation) and us (admin
        // order page + invoice/packing slip), regardless of whether
        // Shiprocket's drawer or the invoice app renders line item
        // properties.
        await this.updateCartNote();

        // Sync header cart-count/theme state, THEN open the Smart Cart
        // drawer directly — no page redirect, so the customer sees their
        // completed box immediately without leaving the page.
        await this.refreshCartState();
        this.openSmartCartDrawer();
        this.setSubmitting(false);
      } catch (err) {
        this.setSubmitting(false);
        this.showError(err.message || 'Something went wrong. Please try again.');
      }
    }
  }

  /** Initialize one controller per BYOB section on the page. */
  function initAll() {
    document.querySelectorAll('[data-byob-builder]').forEach((root) => {
      if (root.dataset.byobInitialized) return; // guard against double-init
      root.dataset.byobInitialized = 'true';
      new BYOBBuilder(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init if the section is added/edited live in the Theme Editor.
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector('[data-byob-builder]');
    if (root && !root.dataset.byobInitialized) {
      root.dataset.byobInitialized = 'true';
      new BYOBBuilder(root);
    }
  });
})();